import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { addStyles, EditableMathField, StaticMathField } from "react-mathquill";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  TextField,
  Modal,
  Typography,
  Button,
  Snackbar,
  Alert,
  IconButton,
  CircularProgress,
  Box,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import TagModal from "./TagModal";
import StyleIcon from "@mui/icons-material/Style";
import katex from "katex";
import "katex/dist/katex.min.css";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import sanitizeHtml from "sanitize-html";
import { getToken } from "../utils/Authe";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const FIB_ENDPOINT = "/subfolder";

// Import the Clipboard module from Quill
const Clipboard = Quill.import("modules/clipboard");

class CustomClipboard extends Clipboard {
  onPaste(e) {
    e.preventDefault();
    const clipboardData = e.clipboardData || window.clipboardData;
    const html = clipboardData.getData("text/html");
    if (html) {
      const sanitizedHtml = sanitizeHtml(html, {
        allowedTags: sanitizeHtml.defaults.allowedTags.concat([
          "b", "i", "em", "strong", "span", "br", "u", "sub", "sup", "a", "p", "img",
        ]),
        allowedAttributes: false,
      });
      const range = this.quill.getSelection();
      if (range) {
        this.quill.clipboard.dangerouslyPasteHTML(range.index, sanitizedHtml);
      }
    } else {
      const text = clipboardData.getData("text/plain");
      const formattedText = `<p>${text.replace(/\n/g, "<br>")}</p>`;
      const range = this.quill.getSelection();
      if (range) {
        this.quill.clipboard.dangerouslyPasteHTML(range.index, formattedText);
      }
    }
  }
}

Quill.register("modules/clipboard", CustomClipboard);
addStyles();

function FillInTheBlanks() {
  // State declarations
  const theme = useTheme();
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [folderName, setFolderName] = useState("Loading...");
  const [cards, setCards] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [questionsList, setQuestionsList] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(null);
  const [currentAnswers, setCurrentAnswers] = useState([]);
  const [currentOptions, setCurrentOptions] = useState([]);
  const [currentExplanation, setCurrentExplanation] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteCardId, setDeleteCardId] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editCard, setEditCard] = useState(null);
  const [editQuestion, setEditQuestion] = useState("");
  const [editAnswers, setEditAnswers] = useState("");
  const [editExplanation, setEditExplanation] = useState("");
  const [selectedText, setSelectedText] = useState("");
  const [isExplanation, setIsExplanation] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [tagCardIndex, setTagCardIndex] = useState(null);
  const [tagModalPosition, setTagModalPosition] = useState({ top: 0, left: 0 });
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorPosition, setEditorPosition] = useState({ top: 0, left: 0 });
  const [currentTextField, setCurrentTextField] = useState(null);
  const [editOptions, setEditOptions] = useState([]);
  const [editIsCorrect, setEditIsCorrect] = useState([]);
  const [editOptionExplanations, setEditOptionExplanations] = useState([]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [popoverContent, setPopoverContent] = useState("");
  const [editingField, setEditingField] = useState(null);
  const [selectionRange, setSelectionRange] = useState(null);
  const [collapsedQuestion, setCollapsedQuestion] = useState(false);
  const [collapsedAnswer, setCollapsedAnswer] = useState(false);
  const [collapsedExplanation, setCollapsedExplanation] = useState(false);
  const [questionCharCount, setQuestionCharCount] = useState(0);
  const [explanationCharCount, setExplanationCharCount] = useState(0);
  const CHAR_LIMIT = 255;

  const questionRef = useRef(null);
  const explanationRef = useRef(null);
  const token = getToken();
  const { id } = useParams();

  const [permissions, setPermissions] = useState({
    can_create_fib: true,
    can_read_fib: true,
    can_update_fib: true,
    can_delete_fib: true,
    can_start_flashcard: true,
    can_start_quiz: true,
    can_tag_fib: true,
    is_admin: true,
  });

  // API Functions
  const fetchFIBQuestions = async (subfolderId) => {
    try {
      const response = await fetch(`${API_BASE_URL}${FIB_ENDPOINT}/${subfolderId}/fib/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch FIB questions: ${response.status}`);
      }
      const data = await response.json();
      return data.map((item) => ({
        id: item.id,
        question: item.statement,
        answer: item.answers,
        explanation: item.explanation || "",
        tags: item.tags || [],
        expanded: false,
      }));
    } catch (error) {
      console.error("Error fetching FIB questions:", error);
      showSnackbar(`Error fetching FIB questions: ${error.message}`, "error");
      return [];
    }
  };

  const createFIBQuestion = async (subfolderId, questionData) => {
    try {
      const response = await fetch(`${API_BASE_URL}${FIB_ENDPOINT}/${subfolderId}/fib/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          statement: questionData.statement,
          question_type: "FIB",
          answers: questionData.answers,
          explanation: questionData.explanation,
          tags: questionData.tags || [],
        }),
      });
      if (!response.ok) {
        throw new Error(`Failed to create FIB question: ${response.status}`);
      }
      const data = await response.json();
      showSnackbar("FIB question created successfully!", "success");
      return {
        id: data.id,
        question: data.statement,
        answer: data.answers,
        explanation: data.explanation || "",
        tags: data.tags || [],
        expanded: false,
      };
    } catch (error) {
      console.error("Error creating FIB question:", error);
      showSnackbar(`Error creating FIB question: ${error.message}`, "error");
      return null;
    }
  };

  const updateFIBQuestion = async (subfolderId, questionId, questionData) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}${FIB_ENDPOINT}/${subfolderId}/fib/${questionId}/`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            statement: questionData.statement,
            question_type: "FIB",
            answers: questionData.answers,
            explanation: questionData.explanation,
            tags: questionData.tags || [],
          }),
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to update FIB question: ${response.status}`);
      }
      const data = await response.json();
      showSnackbar("FIB question updated successfully!", "success");
      return {
        id: data.id,
        question: data.statement,
        answer: data.answers,
        explanation: data.explanation || "",
        tags: data.tags || [],
        expanded: false,
      };
    } catch (error) {
      console.error("Error updating FIB question:", error);
      showSnackbar(`Error updating FIB question: ${error.message}`, "error");
      return null;
    }
  };

  const deleteFIBQuestion = async (subfolderId, questionId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}${FIB_ENDPOINT}/${subfolderId}/fib/${questionId}/`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to delete FIB question: ${response.status}`);
      }
      showSnackbar("FIB question deleted successfully!", "success");
      return true;
    } catch (error) {
      console.error("Error deleting FIB question:", error);
      showSnackbar(`Error deleting FIB question: ${error.message}`, "error");
      return false;
    }
  };

  const fetchFolderName = async (folderId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/folders/${folderId}/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch folder name: ${response.status}`);
      }
      const data = await response.json();
      return data.name;
    } catch (error) {
      console.error("Error fetching folder name:", error);
      return "Unknown Folder";
    }
  };

  // Load questions on component mount
  useEffect(() => {
    const loadData = async () => {
      const name = await fetchFolderName(id);
      setFolderName(name);
      const questions = await fetchFIBQuestions(id);
      if (questions) {
        setCards(questions);
      }
      setIsLoading(false);
    };
    loadData();
  }, [id]);

  // Handlers
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const showSnackbar = (message, severity = "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleTextSelect = () => {
    const textArea = document.getElementById("questionTextField");
    const start = textArea.selectionStart;
    const end = textArea.selectionEnd;
    const selected = textArea.value.substring(start, end);
    setSelectedText(selected);
  };

  const handleExplanationSelect = () => {
    const textArea = document.getElementById("explanationTextField");
    const start = textArea.selectionStart;
    const end = textArea.selectionEnd;
    const selected = textArea.value.substring(start, end);
    setSelectedText(selected);
  };

  const toggleCardExpand = (index) => {
    const newCards = [...cards];
    newCards[index].expanded = !newCards[index].expanded;
    setCards(newCards);
  };

  const openTagModal = (index, event) => {
    if (!permissions.can_tag_fib && !permissions.is_admin) {
      showSnackbar("You don't have permission to tag FIB Questions.", "warning");
      return;
    }
    const iconRect = event.target.getBoundingClientRect();
    setTagCardIndex(index);
    setTagModalPosition({
      top: iconRect.bottom + window.scrollY,
      left: iconRect.left + window.scrollX,
    });
    setTagModalOpen(true);
  };

  const openDeleteDialog = (id) => {
    if (!permissions.can_delete_fib && !permissions.is_admin) {
      showSnackbar("You don't have permission to delete FIB Questions.", "warning");
      return;
    }
    setDeleteCardId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCard = async () => {
    const subfolderId = id;
    const success = await deleteFIBQuestion(subfolderId, deleteCardId);
    if (success) {
      const updatedCards = cards.filter((card) => card.id !== deleteCardId);
      setCards(updatedCards);
      setDeleteDialogOpen(false);
    }
  };

  const handleInputChange = (blankIndex, value) => {
    const newAnswers = [...currentAnswers];
    newAnswers[blankIndex] = value;
    setCurrentAnswers(newAnswers);
  };

  const handleOptionChange = (blankIndex, optionIndex, field, value) => {
    const newOptions = [...currentOptions];
    if (!newOptions[blankIndex]) {
      newOptions[blankIndex] = [];
    }
    if (!newOptions[blankIndex][optionIndex]) {
      newOptions[blankIndex][optionIndex] = {
        answer: "",
        explanation: "",
        is_correct: false,
      };
    }
    newOptions[blankIndex][optionIndex][field] = value;
    setCurrentOptions(newOptions);
  };

  const addOption = (blankIndex) => {
    const newOptions = [...currentOptions];
    if (!newOptions[blankIndex]) {
      newOptions[blankIndex] = [];
    }
    newOptions[blankIndex].push({
      answer: "",
      explanation: "",
      is_correct: false,
    });
    setCurrentOptions(newOptions);
  };

  const openEditDialog = (card) => {
    if (!permissions.can_update_fib && !permissions.is_admin) {
      showSnackbar("You don't have permission to update FIB Questions.", "warning");
      return;
    }
    setEditCard(card);
    setEditQuestion(card.question);
    setEditExplanation(card.explanation || "");
    setEditOptions(card.answer.map((opt) => opt.answer || ""));
    setEditIsCorrect(card.answer.map((opt) => opt.is_correct || false));
    setEditOptionExplanations(card.answer.map((opt) => opt.explanation || ""));
    setEditDialogOpen(true);
  };

  const handleEditOptionChange = (index, value) => {
    const newOptions = [...editOptions];
    newOptions[index] = value;
    setEditOptions(newOptions);
  };

  const handleEditCorrectChange = (index, checked) => {
    const newCorrect = [...editIsCorrect];
    newCorrect[index] = checked;
    setEditIsCorrect(newCorrect);
  };

  const handleEditExplanationChange = (index, value) => {
    const newExplanations = [...editOptionExplanations];
    newExplanations[index] = value;
    setEditOptionExplanations(newExplanations);
  };

  const handleEditRemoveOption = (index) => {
    setEditOptions(editOptions.filter((_, i) => i !== index));
    setEditIsCorrect(editIsCorrect.filter((_, i) => i !== index));
    setEditOptionExplanations(editOptionExplanations.filter((_, i) => i !== index));
  };

  const addEditOption = () => {
    setEditOptions([...editOptions, ""]);
    setEditIsCorrect([...editIsCorrect, false]);
    setEditOptionExplanations([...editOptionExplanations, ""]);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleTagAdded = (cardId, newTag) => {
    if (!cardId || !newTag || !newTag.name) {
      showSnackbar("Invalid tag data. Please try again.", "error");
      return;
    }
    const updatedCards = cards.map((card) =>
      card.id === cardId
        ? {
          ...card,
          tags: [...card.tags, { name: newTag.name, id: newTag.id || Date.now() }],
        }
        : card
    );
    setCards(updatedCards);
    if (editCard && editCard.id === cardId) {
      setEditCard({
        ...editCard,
        tags: [
          ...editCard.tags,
          { name: newTag.name, id: newTag.id || Date.now() },
        ],
      });
    }
    setTagModalOpen(false);
    showSnackbar("Tag added successfully!", "success");
  };

  const handleTagUpdated = (cardId, updatedTag) => {
    if (!cardId || !updatedTag || !updatedTag.id || !updatedTag.name) {
      showSnackbar("Invalid tag update data. Please try again.", "error");
      return;
    }
    const updatedCards = cards.map((card) =>
      card.id === cardId
        ? {
          ...card,
          tags: card.tags.map((tag) =>
            tag.id === updatedTag.id ? { ...tag, name: updatedTag.name } : tag
          ),
        }
        : card
    );
    setCards(updatedCards);
    if (editCard && editCard.id === cardId) {
      setEditCard({
        ...editCard,
        tags: editCard.tags.map((tag) =>
          tag.id === updatedTag.id ? { ...tag, name: updatedTag.name } : tag
        ),
      });
    }
    showSnackbar("Tag updated successfully!", "success");
  };

  const handleTagDeleted = (cardId, tagId) => {
    if (!cardId || !tagId) {
      showSnackbar("Invalid tag deletion data. Please try again.", "error");
      return;
    }
    const updatedCards = cards.map((card) =>
      card.id === cardId
        ? { ...card, tags: card.tags.filter((tag) => tag.id !== tagId) }
        : card
    );
    setCards(updatedCards);
    if (editCard && editCard.id === cardId) {
      setEditCard({
        ...editCard,
        tags: editCard.tags.filter((tag) => tag.id !== tagId),
      });
    }
    showSnackbar("Tag deleted successfully!", "success");
  };

  const stripHtmlTags = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  };

  const handleUpdateQuestion = async () => {
    if (!permissions.can_update_fib && !permissions.is_admin) {
      showSnackbar("You don't have permission to update FIB Questions.", "warning");
      return;
    }
    const strippedQuestion = stripHtmlTags(editQuestion);
    const questionData = {
      statement: editQuestion, // Send as-is
      answers: editOptions.map((opt, idx) => ({
        answer: opt,
        explanation: editOptionExplanations[idx] || "",
        is_correct: editIsCorrect[idx] || false,
      })),
      explanation: editExplanation, // Send as-is
      tags: editCard.tags || [],
    };
    const updatedQuestion = await updateFIBQuestion(
      id,
      editCard.id,
      questionData
    );
    if (updatedQuestion) {
      const updatedCards = cards.map((card) =>
        card.id === editCard.id ? updatedQuestion : card
      );
      setCards(updatedCards);
      setEditDialogOpen(false);
      showSnackbar("Question updated successfully!", "success");
    }
  };

  const handleEditChange = (setter) => (event) => {
    setter(event.target.value);
  };

  const handleQuestionSubmit = () => {
    const blanksCount = (currentQuestion.match(/\[BLANK\]/g) || []).length;
    setQuestionsList([
      ...questionsList,
      {
        currentQuestion,
        answers: new Array(blanksCount).fill(""),
        submitted: false,
        explanation: "",
      },
    ]);
    setCurrentQuestionIndex(questionsList.length);
    setCurrentAnswers(new Array(blanksCount).fill(""));
    setCurrentOptions(new Array(blanksCount).fill([]));
  };

  const handleAnswersSubmit = async () => {
    if (
      !currentQuestion.trim() ||
      currentOptions.some(
        (options) =>
          !options ||
          options.length === 0 ||
          options.some((opt) => !opt.answer.trim())
      )
    ) {
      showSnackbar(
        "Please fill in the question and at least one option for each blank.",
        "warning"
      );
      return;
    }
    // Check if at least one option is marked as correct
    const hasCorrectOption = currentOptions.some((options) =>
      options.some((opt) => opt.is_correct)
    );

    if (!hasCorrectOption) {
      showSnackbar("Please select at least one correct option.", "warning");
      return;
    }
    const strippedQuestion = stripHtmlTags(currentQuestion);
    const isDuplicate = cards.some(
      (card) => stripHtmlTags(card.question).trim() === strippedQuestion.trim()
    );
    if (isDuplicate) {
      setDuplicateDialogOpen(true);
      return;
    }
    const subfolderId = id;
    const questionData = {
      statement: currentQuestion, // Send as-is
      answers: currentOptions.flat().map((opt) => ({
        answer: opt.answer,
        explanation: opt.explanation || "",
        is_correct: opt.is_correct || false,
      })),
      explanation: currentExplanation, // Send as-is
      tags: [],
    };
    const newQuestion = await createFIBQuestion(subfolderId, questionData);
    if (newQuestion) {
      const updatedQuestionsList = [...questionsList];
      updatedQuestionsList[currentQuestionIndex] = {
        ...updatedQuestionsList[currentQuestionIndex],
        answers: currentAnswers,
        explanation: currentExplanation,
        submitted: true,
      };
      setCards([...cards, newQuestion]);
      setQuestionsList(updatedQuestionsList);
      setCurrentAnswers([]);
      setCurrentOptions([]);
      setCurrentQuestion("");
      setCurrentQuestionIndex(null);
      setCurrentExplanation("");
      showSnackbar("Question added successfully!", "success");
    }
  };

  const renderContent = (html) => {
    if (!html) return null;
    const parts = html.split(/(\$\$.+?\$\$|\$.+?\$)/g);
    return (
      <div>
        {parts.map((part, index) => {
          if (part.startsWith("$$") && part.endsWith("$$")) {
            const latex = part.slice(2, -2);
            try {
              const renderedLatex = katex.renderToString(latex, {
                displayMode: true,
              });
              return (
                <div
                  key={index}
                  dangerouslySetInnerHTML={{ __html: renderedLatex }}
                  className="rendered-content block-latex"
                />
              );
            } catch (error) {
              console.error("Block LaTeX rendering error:", error);
              return (
                <div key={index} className="error-content">
                  {part}
                </div>
              );
            }
          }
          if (part.startsWith("$") && part.endsWith("$")) {
            const latex = part.slice(1, -1);
            try {
              const renderedLatex = katex.renderToString(latex, {
                displayMode: false,
              });
              return (
                <span
                  key={index}
                  dangerouslySetInnerHTML={{ __html: renderedLatex }}
                  className="rendered-content inline-latex"
                />
              );
            } catch (error) {
              console.error("Inline LaTeX rendering error:", error);
              return (
                <span key={index} className="error-content">
                  {part}
                </span>
              );
            }
          }
          try {
            const formattedContent = part.replace(/\n/g, "<br />");
            const allowedTags = [
              "b",
              "i",
              "em",
              "strong",
              "span",
              "br",
              "u",
              "sub",
              "sup",
              "a",
              "p",
              "img",
              "h1",
              "h2",
              "h3",
              "h4",
              "h5",
              "h6",
              "ol",
              "ul",
              "li",
              "blockquote",
            ];
            const allowedAttributes = {
              img: ["src", "alt"],
              a: ["href", "target"],
              span: ["style"],
              p: ["style"],
              h1: ["style"],
              h2: ["style"],
              h3: ["style"],
              h4: ["style"],
              h5: ["style"],
              h6: ["style"],
            };
            const sanitizedContent = sanitizeHtml(formattedContent, {
              allowedTags,
              allowedAttributes,
              transformTags: {
                p: "span",
                div: "span",
              },
            });
            return (
              <span
                key={index}
                dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                className="rendered-content inline-text"
              />
            );
          } catch (error) {
            console.error("HTML rendering error:", error);
            return (
              <span key={index} className="error-content">
                {part}
              </span>
            );
          }
        })}
      </div>
    );
  };

  const createBlanks = (question, answers, isCurrent) => {
    const parts = question.split("[BLANK]");
    const blanks = parts.slice(1).map((_, index) => (
      <div key={index} style={{ display: "inline-block", margin: "0 5px" }}>
        _____
      </div>
    ));

    return (
      <span style={{ display: "inline-flex", alignItems: "baseline", whiteSpace: "nowrap" }}>
        {parts.map((part, index) => (
          <React.Fragment key={index}>
            <span style={{ display: "inline", whiteSpace: "nowrap" }}>
              {renderContent(part)}
            </span>
            {index < parts.length - 1 && blanks[index]}
          </React.Fragment>
        ))}
      </span>
    );
  };


  const handleQuestionKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleQuestionSubmit();
    }
  };

  const handleQuestionChange = (value) => {
    let input = value;
    const formattedQuestion = input.replace(/_/g, "[BLANK]");
    setCurrentQuestion(formattedQuestion);
    const text = formattedQuestion;
    setQuestionCharCount(text.length);
  };

  const handleExplanationChange = (value) => {
    setCurrentExplanation(value);
    const text = value;
    setExplanationCharCount(text.length);
  };

  const handleIconClick = () => {
    setDropdownVisible(!dropdownVisible);
  };

  const handleDropdownChange = (e) => {
    const selectedValue = e.target.value;
    setSelectedOption(selectedValue);
    if (
      selectedValue === "practiceCard" &&
      !permissions.can_start_flashcard &&
      !permissions.is_admin
    ) {
      showSnackbar("You don't have permission to start Practice Questions.", "warning");
      return;
    }
    if (
      selectedValue === "quizPractice" &&
      !permissions.can_start_quiz &&
      !permissions.is_admin
    ) {
      showSnackbar("You don't have permission to start Quiz Practice.", "warning");
      return;
    }
    setDropdownVisible(false);
  };

  const handleConfirmDuplicate = async () => {
    const blanksCount = (currentQuestion.match(/\[BLANK\]/g) || []).length;
    const updatedQuestionsList = [...questionsList];
    updatedQuestionsList[currentQuestionIndex] = {
      ...updatedQuestionsList[currentQuestionIndex],
      answers: currentAnswers,
      explanation: currentExplanation,
      submitted: true,
    };
    const subfolderId = id;
    const strippedQuestion = stripHtmlTags(currentQuestion);
    const strippedExplanation = stripHtmlTags(currentExplanation);
    const questionData = {
      statement: strippedQuestion,
      answers: currentOptions
        .flat()
        .map((opt) => ({
          answer: opt.answer,
          explanation: opt.explanation || "",
          is_correct: opt.is_correct || false,
        })),
      explanation: strippedExplanation,
      tags: [],
    };
    const newQuestion = await createFIBQuestion(subfolderId, questionData);
    if (newQuestion) {
      setCards([...cards, newQuestion]);
      setQuestionsList(updatedQuestionsList);
      setCurrentAnswers([]);
      setCurrentOptions([]);
      setCurrentQuestion("");
      setCurrentQuestionIndex(null);
      setCurrentExplanation("");
      setDuplicateDialogOpen(false);
      showSnackbar("Question added successfully!", "success");
    }
  };

  const handleCancelDuplicate = () => {
    setDuplicateDialogOpen(false);
  };

  const toggleQuestion = () => setCollapsedQuestion(!collapsedQuestion);
  const toggleAnswer = () => setCollapsedAnswer(!collapsedAnswer);
  const toggleExplanation = () => setCollapsedExplanation(!collapsedExplanation);

  const styles = {
    requiredAsterisk: {
      color: "red",
      marginLeft: "4px",
      display: "inline-block",
    },
    label: {
      display: "flex",
      alignItems: "center",
      marginBottom: "8px",
    },
    quillContainer: {
      wordBreak: "break-word",
      overflowWrap: "break-word",
      position: "relative",
    },
    tooltipContainer: {
      display: "inline-block",
      width: "100%",
    },
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ background: theme.palette.background.default, }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: theme.palette.background.default,
        padding: "20px 20px",
      }}
    >
      <style>
        {`
    ${theme.palette.mode === 'dark' ? `
      /* Default toolbar icon colors */
      .ql-toolbar .ql-stroke {
        stroke: #e5e7eb !important;
      }
      .ql-toolbar .ql-fill {
        fill: #e5e7eb !important;
      }
      .ql-toolbar .ql-picker-label {
        color: #e5e7eb !important;
      }

      /* Hover and focus states */
      .ql-toolbar button:hover .ql-stroke,
      .ql-toolbar button:focus .ql-stroke {
        stroke: #667eea !important;
      }
      .ql-toolbar button:hover .ql-fill,
      .ql-toolbar button:focus .ql-fill {
        fill: #667eea !important;
      }
      .ql-toolbar button:hover,
      .ql-toolbar button:focus {
        background-color: rgba(102, 126, 234, 0.2) !important;
      }

      /* Active state (when button is clicked/selected) */
      .ql-toolbar button.ql-active .ql-stroke {
        stroke: #667eea !important;
      }
      .ql-toolbar button.ql-active .ql-fill {
        fill: #667eea !important;
      }
      .ql-toolbar button.ql-active {
        background-color: rgba(102, 126, 234, 0.3) !important;
      }

      /* Editor placeholder and text color */
      .ql-editor.ql-blank::before {
        color: #9ca3af !important;
      }
      .ql-editor {
        color: #e5e7eb !important;
      }

      /* Toolbar and container border */
      .ql-toolbar.ql-snow,
      .ql-container.ql-snow {
        border-color: rgba(255, 255, 255, 0.12) !important;
      }
    ` : ''}
  `}
      </style>

      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          background: theme.palette.background.paper,
          borderRadius: "24px",
          padding: "40px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.1)",
        }}
      >
        {/* Header Section */}
        <div
          style={{
            marginBottom: "40px",
            paddingBottom: "30px",
            borderBottom: "2px solid #e5e7eb",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "20px",
            }}
          >
            <div>
              <h1
                style={{
                  margin: "0 0 10px 0",
                  fontSize: "2.5rem",
                  fontWeight: "800",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Fill in the Blanks Questions
              </h1>
              <div
                style={{
                  fontSize: "1.25rem",
                  color: theme.palette.text.secondary,
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <i className="bi bi-folder2-open" style={{ color: "#667eea" }}></i>
                {folderName}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Tooltip title="KaTeX Documentation" arrow>
                <IconButton
                  onClick={() => window.open("https://katex.org/docs/supported.html", "_blank")}
                  sx={{
                    bgcolor: "rgba(102, 126, 234, 0.1)",
                    "&:hover": { bgcolor: "rgba(102, 126, 234, 0.2)" },
                    p: 1.5,
                    borderRadius: "50%",
                  }}
                >
                  <HelpOutlineIcon sx={{ color: "#667eea" }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="User Guide" arrow>
                <IconButton
                  onClick={() => setInfoModalOpen(true)}
                  sx={{
                    bgcolor: "rgba(102, 126, 234, 0.1)",
                    "&:hover": { bgcolor: "rgba(102, 126, 234, 0.2)" },
                    p: 1.5,
                    borderRadius: "50%",
                  }}
                >
                  <InfoOutlinedIcon sx={{ color: "#667eea" }} />
                </IconButton>
              </Tooltip>
            </div>

          </div>
        </div>


        {/* Input Form Section */}
        <div
          style={{
            background: theme.palette.mode === 'light'
              ? "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)"
              : "transparent",
            borderRadius: "16px",
            padding: "30px",
            marginBottom: "40px",
            border: "2px solid #e0e7ff",
          }}
        >
          {/* Question Field */}
          <div style={{ marginBottom: "24px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                marginBottom: "12px",
                padding: "12px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                borderRadius: "10px",
                transition: "all 0.3s ease",
              }}
              onClick={toggleQuestion}
            >
              <div style={{ marginRight: "12px", fontSize: "1.2rem" }}>
                {collapsedQuestion ? <FaChevronDown /> : <FaChevronUp />}
              </div>
              <div style={{ fontWeight: "600", fontSize: "1.1rem" }}>
                Question <span style={{ color: "#ef4444" }}>*</span>
              </div>
            </div>
            <div style={{ display: collapsedQuestion ? "none" : "block", position: "relative" }}>
              <div
                style={{
                  borderRadius: "12px",
                  overflow: "hidden",
                  border: "2px solid #e0e7ff",
                  background: theme.palette.background.default,
                  color: theme.palette.text.primary,
                }}
              >
                <ReactQuill
                  id="questionTextField"
                  value={currentQuestion}
                  onChange={handleQuestionChange}
                  onKeyDown={handleQuestionKeyDown}
                  placeholder="Enter your question with blanks as '_', then bring the cursor at the end and press enter to add option(s)"
                  modules={{
                    toolbar: [
                      ["bold", "italic", "underline", "strike"],
                      ["blockquote", "code-block"],
                      [{ header: 1 }, { header: 2 }],
                      [{ list: "ordered" }, { list: "bullet" }],
                      [{ script: "sub" }, { script: "super" }],
                      [{ indent: "-1" }, { indent: "+1" }],
                      [{ direction: "rtl" }],
                      [{ size: ["small", false, "large", "huge"] }],
                      [{ header: [1, 2, 3, 4, 5, 6, false] }],
                      [{ color: [] }, { background: [] }],
                      [{ font: [] }],
                      [{ align: [] }],
                      ["clean"],
                    ],
                  }}
                  formats={[
                    "header",
                    "font",
                    "size",
                    "bold",
                    "italic",
                    "underline",
                    "strike",
                    "blockquote",
                    "code-block",
                    "list",
                    "bullet",
                    "indent",
                    "link",
                    "image",
                    "video",
                    "color",
                    "background",
                    "align",
                    "script",
                    { name: "preserve-spaces", type: "inline" },
                  ]}
                  style={{
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "pre-wrap",
                  }}
                />
              </div>
              <div
                style={{
                  position: "absolute",
                  bottom: "15px",
                  right: "15px",
                  fontSize: "13px",
                  fontWeight: "600",
                  background: "rgba(102, 126, 234, 0.1)",
                  padding: "6px 12px",
                  borderRadius: "20px",
                  color: questionCharCount > CHAR_LIMIT * 0.9 ? "#ef4444" : "#667eea",
                }}
              >
                {questionCharCount}/{CHAR_LIMIT}
              </div>
            </div>
          </div>

          {questionsList.map(
            (q, index) =>
              !q.submitted && (
                <div
                  key={index}
                  className="question-container"
                  style={{
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    background: theme.palette.background.default,
                    borderRadius: "16px",
                    padding: "20px",
                    marginBottom: "20px",
                    border: "2px solid #e0e7ff",
                  }}
                >
                  <p
                    style={{
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      fontSize: "1.1rem",
                      lineHeight: "1.7",
                      color: theme.palette.text.primary,
                    }}
                  >
                    Q. {createBlanks(q.currentQuestion, q.answers, index === currentQuestionIndex)}
                  </p>
                  {index === currentQuestionIndex && (
                    <div style={{ marginTop: "20px" }}>
                      <div
                        style={{
                          fontSize: "1.1rem",
                          fontWeight: "600",
                          marginBottom: "10px",
                          color: theme.palette.text.primary,
                        }}
                      >
                        Options
                      </div>
                      {currentOptions.map((options, blankIndex) => (
                        <div key={blankIndex} style={{ marginBottom: "20px" }}>
                          <div
                            style={{
                              fontSize: "1rem",
                              fontWeight: "600",
                              marginBottom: "10px",
                              color: theme.palette.text.primary,
                            }}
                          >
                            Blank {blankIndex + 1}
                          </div>
                          {(options || []).map((option, optIndex) => (
                            <div
                              key={optIndex}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "10px",
                                padding: "10px",
                                border: "1px solid #e5e7eb",
                                borderRadius: "8px",
                                background: theme.palette.background.paper,
                                color: theme.palette.text.primary,
                              }}
                            >
                              <ReactQuill
                                value={option.answer || ""}
                                onChange={(value) =>
                                  handleOptionChange(blankIndex, optIndex, "answer", value)}
                                placeholder="Enter your option here"
                                modules={{
                                  toolbar: [
                                    ["bold", "italic", "underline", "strike"],
                                    ["blockquote", "code-block"],
                                    [{ list: "ordered" }, { list: "bullet" }],
                                    [{ script: "sub" }, { script: "super" }],
                                    ["clean"],
                                  ],
                                }}
                                style={{ marginRight: "10px", flex: 1 }}
                              />
                              <ReactQuill
                                value={option.explanation || ""}
                                onChange={(value) =>
                                  handleOptionChange(blankIndex, optIndex, "explanation", value)}
                                placeholder="Explanation for this option"
                                modules={{
                                  toolbar: [
                                    ["bold", "italic", "underline", "strike"],
                                    ["blockquote", "code-block"],
                                    [{ list: "ordered" }, { list: "bullet" }],
                                    [{ script: "sub" }, { script: "super" }],
                                    ["clean"],
                                  ],
                                }}
                                style={{ marginRight: "10px", flex: 1 }}
                              />
                              <label style={{ display: "flex", alignItems: "center", marginRight: "10px" }}>
                                <input
                                  type="checkbox"
                                  checked={option.is_correct || false}
                                  onChange={(e) =>
                                    handleOptionChange(
                                      blankIndex,
                                      optIndex,
                                      "is_correct",
                                      e.target.checked
                                    )
                                  }
                                  style={{ marginRight: "5px" }}
                                />
                                Correct
                              </label>
                              <IconButton
                                aria-label="delete"
                                onClick={() => {
                                  const newOptions = [...currentOptions];
                                  newOptions[blankIndex].splice(optIndex, 1);
                                  setCurrentOptions(newOptions);
                                }}
                                style={{ color: "#ef4444" }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </div>
                          ))}
                          <Button
                            variant="outlined"
                            onClick={() => addOption(blankIndex)}
                            sx={{
                              marginTop: "10px",
                              color: "#667eea",
                              borderColor: "#667eea",
                              "&:hover": {
                                backgroundColor: "rgba(102, 126, 234, 0.1)",
                                borderColor: "#667eea",
                              },
                            }}
                          >
                            Add Option
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
          )}

          {/* Submit Button */}
          <div style={{ textAlign: "center", marginTop: "30px" }}>
            <Button
              onClick={handleAnswersSubmit}
              variant="contained"
              disabled={!(permissions.can_create_fib || permissions.is_admin)}
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                padding: "14px 40px",
                fontSize: "1.1rem",
                fontWeight: "700",
                borderRadius: "12px",
                textTransform: "none",
                boxShadow: "0 8px 25px rgba(102, 126, 234, 0.4)",
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                  transform: "translateY(-3px)",
                  boxShadow: "0 12px 35px rgba(102, 126, 234, 0.5)",
                },
                "&.Mui-disabled": {
                  background: "#d1d5db",
                  color: "white",
                  opacity: 0.6,
                },
              }}
            >
              <i className="bi bi-plus-circle" style={{ marginRight: "10px", fontSize: "1.2rem" }}></i>
              Add Question
            </Button>
          </div>
        </div>

        {/* Questions List */}


        {/* Flashcards List */}
        {permissions.can_read_fib || permissions.is_admin ? (
          <div>
            {cards.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  background: theme.palette.mode === 'light'
                    ? "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)"
                    : theme.palette.background.default,
                  borderRadius: "16px",
                  border: "2px dashed #c7d2fe",
                }}
              >
                <i
                  className="bi bi-inbox"
                  style={{
                    fontSize: "4rem",
                    color: "#c7d2fe",
                    marginBottom: "20px",
                    display: "block",
                  }}
                ></i>
                <p
                  style={{
                    fontSize: "1.3rem",
                    color: "#6b7280",
                    fontWeight: "600",
                    margin: 0,
                  }}
                >
                  No Questions yet. Create your first one above!
                </p>
              </div>
            ) : (
              <ul style={{ padding: 0, listStyle: "none", margin: 0 }}>
                {cards.map((card, index) => (
                  <li
                    key={card.id}
                    style={{
                      position: "relative",
                      padding: "28px",
                      borderRadius: "16px",
                      marginBottom: "20px",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "pre-wrap",
                      border: "2px solid #e5e7eb",
                      background: theme.palette.background.paper,
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = "0 8px 30px rgba(102, 126, 234, 0.2)";
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.borderColor = "#c7d2fe";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = "0 4px 20px rgba(0, 0, 0, 0.08)";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.borderColor = "#e5e7eb";
                    }}
                  >
                    {/* Question Section */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        paddingRight: "60px",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      <div
                        style={{
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          color: "white",
                          padding: "8px 14px",
                          borderRadius: "10px",
                          fontWeight: "800",
                          fontSize: "1.1rem",
                          marginRight: "16px",
                          minWidth: "45px",
                          textAlign: "center",
                          boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                        }}
                      >
                        Q
                      </div>
                      <span
                        style={{
                          flex: 1,
                          color: theme.palette.text.primary,
                          lineHeight: "1.7",
                          fontSize: "1.05rem",
                          fontWeight: "600",
                        }}
                      >
                        {renderContent(card.question.replace(/\[BLANK\]/g, "_____"))}
                      </span>
                    </div>

                    {/* Action Icons */}
                    <div
                      style={{
                        position: "absolute",
                        top: "20px",
                        right: "20px",
                        display: "flex",
                        gap: "14px",
                        zIndex: 10,
                        background: theme.palette.background.default,
                        padding: "10px 14px",
                        borderRadius: "12px",
                        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      <i
                        className={`bi ${card.expanded ? "bi-arrows-angle-contract" : "bi-arrows-angle-expand"
                          }`}
                        title={card.expanded ? "Contract" : "Expand"}
                        onClick={() => toggleCardExpand(index)}
                        style={{
                          cursor: "pointer",
                          fontSize: "1.2rem",
                          color: "#667eea",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "#764ba2";
                          e.currentTarget.style.transform = "scale(1.2)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#667eea";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      ></i>
                      <i
                        className="bi bi-pencil-square"
                        title="Edit"
                        onClick={() => openEditDialog(card)}
                        style={{
                          cursor: permissions.can_update_fib || permissions.is_admin ? "pointer" : "not-allowed",
                          opacity: permissions.can_update_fib || permissions.is_admin ? 1 : 0.4,
                          fontSize: "1.2rem",
                          color: "#10b981",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (permissions.can_update_fib || permissions.is_admin) {
                            e.currentTarget.style.color = "#059669";
                            e.currentTarget.style.transform = "scale(1.2)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#10b981";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      ></i>
                      <i
                        className="bi bi-trash3"
                        title="Delete"
                        onClick={() => openDeleteDialog(card.id)}
                        style={{
                          cursor: permissions.can_delete_fib || permissions.is_admin ? "pointer" : "not-allowed",
                          opacity: permissions.can_delete_fib || permissions.is_admin ? 1 : 0.4,
                          fontSize: "1.2rem",
                          color: "#ef4444",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (permissions.can_delete_fib || permissions.is_admin) {
                            e.currentTarget.style.color = "#dc2626";
                            e.currentTarget.style.transform = "scale(1.2)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#ef4444";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      ></i>
                      {/* <i
                        className="bi bi-tags"
                        title="Tag"
                        onClick={(e) => openTagModal(index, e)}
                        style={{
                          cursor: permissions.can_tag_fib || permissions.is_admin ? "pointer" : "not-allowed",
                          opacity: permissions.can_tag_fib || permissions.is_admin ? 1 : 0.4,
                          fontSize: "1.2rem",
                          color: "#8b5cf6",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (permissions.can_tag_fib || permissions.is_admin) {
                            e.currentTarget.style.color = "#7c3aed";
                            e.currentTarget.style.transform = "scale(1.2)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#8b5cf6";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      ></i> */}
                    </div>

                    {/* Expanded Content */}
                    {card.expanded && (permissions.can_read_fib || permissions.is_admin) && (
                      <div
                        style={{
                          marginTop: "30px",
                          paddingTop: "24px",
                          borderTop: "2px solid #e5e7eb",
                        }}
                      >
                        {/* Answer */}
                        {card.answer.map((ans, idx) => (
                          <div
                            key={idx}
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                              whiteSpace: "pre-wrap",
                              marginBottom: "20px",
                            }}
                          >
                            <div
                              style={{
                                background: ans.is_correct
                                  ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                                  : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                                color: "white",
                                padding: "8px 14px",
                                borderRadius: "10px",
                                fontWeight: "800",
                                fontSize: "1.1rem",
                                marginRight: "16px",
                                minWidth: "45px",
                                textAlign: "center",
                                boxShadow: ans.is_correct
                                  ? "0 4px 15px rgba(16, 185, 129, 0.3)"
                                  : "0 4px 15px rgba(239, 68, 68, 0.3)",
                              }}
                            >
                              {ans.is_correct ? "✓" : "X"}
                            </div>
                            <span
                              style={{
                                flex: 1,
                                color: theme.palette.text.secondary,
                                lineHeight: "1.7",
                                fontSize: "1.02rem",
                              }}
                            >
                              {renderContent(ans.answer)}
                            </span>
                            {ans.explanation && (
                              <div
                                style={{
                                  marginLeft: "30px",
                                  marginTop: "2px",
                                  display: "flex",
                                  alignItems: "flex-start",
                                }}
                              >
                                <strong>Explanation: </strong>
                                <span style={{ marginLeft: "5px" }}>
                                  {renderContent(ans.explanation)}
                                </span>
                              </div>
                            )}
                          </div>
                        ))}

                        {/* Explanation */}
                        {card.explanation && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              wordBreak: "break-word",
                              overflowWrap: "break-word",
                              whiteSpace: "pre-wrap",
                              marginBottom: "20px",
                              padding: "16px 20px",
                              background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                              borderRadius: "12px",
                              borderLeft: "4px solid #10b981",
                              boxShadow: "0 2px 10px rgba(16, 185, 129, 0.1)",
                            }}
                          >
                            <div
                              style={{
                                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                                color: "white",
                                padding: "6px 12px",
                                borderRadius: "8px",
                                fontWeight: "700",
                                fontSize: "0.9rem",
                                marginRight: "14px",
                                minWidth: "fit-content",
                                whiteSpace: "nowrap",
                              }}
                            >
                              💡 Explanation
                            </div>
                            <span
                              style={{
                                flex: 1,
                                color: "#065f46",
                                lineHeight: "1.7",
                                fontSize: "1rem",
                              }}
                            >
                              {renderContent(card.explanation)}
                            </span>
                          </div>
                        )}

                        {/* Tags */}
                        {/* <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            marginTop: "20px",
                            flexWrap: "wrap",
                            padding: "16px",
                            background: "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)",
                            borderRadius: "12px",
                            border: "2px solid #e9d5ff",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              color: "#7c3aed",
                              fontWeight: "700",
                              fontSize: "1rem",
                            }}
                          >
                            <i className="bi bi-tags-fill"></i>
                            Tags:
                          </div>
                          {card.tags.length === 0 ? (
                            <span
                              style={{
                                color: "#9ca3af",
                                fontStyle: "italic",
                                fontSize: "0.95rem",
                              }}
                            >
                              No tags added yet
                            </span>
                          ) : (
                            card.tags.map((tag) => (
                              <span
                                key={tag.id}
                                style={{
                                  background: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
                                  color: "white",
                                  padding: "6px 16px",
                                  borderRadius: "20px",
                                  fontSize: "0.9rem",
                                  fontWeight: "600",
                                  boxShadow: "0 3px 10px rgba(139, 92, 246, 0.3)",
                                  transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = "translateY(-2px)";
                                  e.currentTarget.style.boxShadow = "0 5px 15px rgba(139, 92, 246, 0.4)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = "translateY(0)";
                                  e.currentTarget.style.boxShadow = "0 3px 10px rgba(139, 92, 246, 0.3)";
                                }}
                              >
                                {tag.name}
                              </span>
                            ))
                          )}
                        </div> */}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "60px 20px",
              background: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
              borderRadius: "16px",
              border: "2px solid #fecaca",
            }}
          >
            <i
              className="bi bi-shield-lock"
              style={{
                fontSize: "4rem",
                color: "#fca5a5",
                marginBottom: "20px",
                display: "block",
              }}
            ></i>
            <p
              style={{
                fontSize: "1.3rem",
                color: "#dc2626",
                fontWeight: "600",
                margin: 0,
              }}
            >
              You don't have permission to view FIB Questions.
            </p>
          </div>
        )}

        {/* Delete Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          maxWidth="xs"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: "12px",
              padding: "10px",
            },
          }}
        >
          <DialogTitle
            id="alert-dialog-title"
            sx={{
              fontWeight: "bold",
              color: "error.main",
              fontSize: "1.3rem",
              display: "flex",
              alignItems: "center",
            }}
          >
            <DeleteIcon sx={{ color: "error.main", mr: 1 }} />
            Delete Question
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                Are you sure you want to delete this Question? <br />
                This action cannot be undone.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
            <Button
              onClick={handleCloseDeleteDialog}
              variant="outlined"
              color="inherit"
              sx={{
                borderColor: "grey.400",
                padding: "8px 20px",
                fontSize: "0.9rem",
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteCard}
              disabled={!(permissions.can_delete_fib || permissions.is_admin)}
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              sx={{
                boxShadow: "none",
                padding: "8px 20px",
                fontSize: "0.9rem",
                textTransform: "none",
              }}
            >
              Delete Permanently
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          PaperProps={{
            style: {
              borderRadius: "20px",
              padding: "10px 0",
              minWidth: "600px",
              maxWidth: "90vw",
              "&::-webkit-scrollbar": {
                width: "10px",
                height: "10px",
              },
              "&::-webkit-scrollbar-track": {
                background: theme.palette.mode === 'dark' ? '#2d3748' : '#f1f5f9',
                borderRadius: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: theme.palette.mode === 'dark' ? '#475569' : '#94a3b8',
                borderRadius: "6px",
                border: `2px solid ${theme.palette.mode === 'dark' ? '#2d3748' : '#f1f5f9'}`,
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: theme.palette.mode === 'dark' ? '#64748b' : '#64748b',
              },
              // Custom scrollbar for Firefox
              scrollbarWidth: "thin",
              scrollbarColor: theme.palette.mode === 'dark' ? '#475569 #2d3748' : '#94a3b8 #f1f5f9',
            }
          }
          }
        >
          <DialogTitle
            style={{
              fontSize: "1.8rem",
              fontWeight: "800",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              paddingBottom: "10px",
              borderBottom: "2px solid #e5e7eb",
            }}
          >
            <i className="bi bi-pencil-square" style={{ marginRight: "10px" }}></i>
            Edit Question
          </DialogTitle>
          <DialogContent style={{ paddingTop: "24px" }}>
            <DialogContentText
              style={{
                marginBottom: "24px",
                color: theme.palette.text.secondary,
                fontSize: "1.05rem",
              }}
            >
              Update your Question details below.
            </DialogContentText>
            <div style={{ marginBottom: "24px", position: "relative" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "10px",
                  fontWeight: "700",
                  fontSize: "1.1rem",
                  color: theme.palette.text.primary,
                }}
              >
                Question <span style={{ color: "#ef4444" }}>*</span>
              </label>
              <div
                style={{
                  borderRadius: "12px",
                  overflow: "hidden",
                  border: "2px solid #e0e7ff",
                  background: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                }}
              >
                <ReactQuill
                  value={editQuestion}
                  onChange={setEditQuestion}
                  modules={{
                    toolbar: [
                      ["bold", "italic", "underline", "strike"],
                      ["blockquote", "code-block"],
                      [{ header: 1 }, { header: 2 }],
                      [{ list: "ordered" }, { list: "bullet" }],
                      [{ script: "sub" }, { script: "super" }],
                      [{ indent: "-1" }, { indent: "+1" }],
                      [{ direction: "rtl" }],
                      [{ size: ["small", false, "large", "huge"] }],
                      [{ header: [1, 2, 3, 4, 5, 6, false] }],
                      [{ color: [] }, { background: [] }],
                      [{ font: [] }],
                      [{ align: [] }],
                      ["clean"],
                    ],
                  }}
                  formats={[
                    "header",
                    "font",
                    "size",
                    "bold",
                    "italic",
                    "underline",
                    "strike",
                    "blockquote",
                    "code-block",
                    "list",
                    "bullet",
                    "indent",
                    "link",
                    "image",
                    "video",
                    "color",
                    "background",
                    "align",
                    "script",
                    { name: "preserve-spaces", type: "inline" },
                  ]}
                />
              </div>
            </div>

            <div>
              {editOptions.map((option, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: "20px",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    background: theme.palette.background.default,
                  }}
                >
                  <div style={{ marginBottom: "5px", fontWeight: "bold" }}>Options:</div>
                  <ReactQuill
                    value={option}
                    onChange={(value) => handleEditOptionChange(index, value)}
                    modules={{
                      toolbar: [
                        ["bold", "italic", "underline", "strike"],
                        ["blockquote", "code-block"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        [{ script: "sub" }, { script: "super" }],
                        ["clean"],
                      ],
                    }}
                  />
                  <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                    <label style={{ marginRight: "30px", display: "flex", alignItems: "center" }}>
                      <input
                        type="checkbox"
                        checked={editIsCorrect[index]}
                        onChange={(e) => handleEditCorrectChange(index, e.target.checked)}
                        style={{ marginRight: "8px" }}
                      />
                      Correct Answer
                    </label>
                    <IconButton aria-label="delete" onClick={() => handleEditRemoveOption(index)} sx={{ color: "error.main" }}>
                      <DeleteIcon />
                    </IconButton>
                  </div>
                  <div style={{ marginBottom: "5px", fontWeight: "bold" }}>Explanation</div>
                  <ReactQuill
                    value={editOptionExplanations[index]}
                    onChange={(value) => handleEditExplanationChange(index, value)}
                    modules={{
                      toolbar: [
                        ["bold", "italic", "underline", "strike"],
                        ["blockquote", "code-block"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        [{ script: "sub" }, { script: "super" }],
                        ["clean"],
                      ],
                    }}
                  />
                </div>
              ))}
              <Button onClick={addEditOption} variant="contained" color="primary">
                Add Option
              </Button>
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseEditDialog}
              sx={{
                color: theme.palette.text.primary,
                padding: "10px 24px",
                fontSize: "1rem",
                fontWeight: "600",
                borderRadius: "10px",
                textTransform: "none",
                "&:hover": {
                  background: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.08)'
                      : '#f3f4f6',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateQuestion}
              disabled={!(permissions.can_update_fib || permissions.is_admin)}
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                padding: "10px 28px",
                fontSize: "1rem",
                fontWeight: "700",
                borderRadius: "10px",
                textTransform: "none",
                boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                "&:hover": {
                  background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                  boxShadow: "0 6px 20px rgba(102, 126, 234, 0.5)",
                },
                "&.Mui-disabled": {
                  background: "#d1d5db",
                  color: "white",
                  opacity: 0.6,
                },
              }}
            >
              <i className="bi bi-check-circle" style={{ marginRight: "8px" }}></i>
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Duplicate Dialog */}
        <Dialog
          open={duplicateDialogOpen}
          onClose={handleCancelDuplicate}
          PaperProps={{
            style: {
              borderRadius: "20px",
              padding: "10px",
              minWidth: "500px",
            },
          }}
        >
          <DialogTitle
            style={{
              fontSize: "1.8rem",
              fontWeight: "800",
              color: "#f59e0b",
              paddingBottom: "10px",
              borderBottom: "2px solid #fef3c7",
            }}
          >
            <i className="bi bi-exclamation-circle" style={{ marginRight: "10px" }}></i>
            Duplicate Question
          </DialogTitle>
          <DialogContent style={{ paddingTop: "24px" }}>
            <DialogContentText
              style={{
                color: "#374151",
                fontSize: "1.1rem",
                lineHeight: "1.6",
              }}
            >
              A similar question already exists. Do you want to add it anyway?
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: "20px 24px" }}>
            <Button
              onClick={handleCancelDuplicate}
              sx={{
                color: "#6b7280",
                padding: "10px 24px",
                fontSize: "1rem",
                fontWeight: "600",
                borderRadius: "10px",
                textTransform: "none",
                "&:hover": {
                  background: "#f3f4f6",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDuplicate}
              disabled={!(permissions.can_create_fib || permissions.is_admin)}
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                color: "white",
                padding: "10px 28px",
                fontSize: "1rem",
                fontWeight: "700",
                borderRadius: "10px",
                textTransform: "none",
                boxShadow: "0 4px 15px rgba(245, 158, 11, 0.4)",
                "&:hover": {
                  background: "linear-gradient(135deg, #d97706 0%, #f59e0b 100%)",
                  boxShadow: "0 6px 20px rgba(245, 158, 11, 0.5)",
                },
                "&.Mui-disabled": {
                  background: "#d1d5db",
                  color: "white",
                  opacity: 0.6,
                },
              }}
            >
              <i className="bi bi-plus-circle" style={{ marginRight: "8px" }}></i>
              Add Anyway
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{
              width: "100%",
              fontSize: "1rem",
              fontWeight: "600",
              borderRadius: "12px",
              boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)",
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        {/* Tag Modal */}
        <TagModal
          open={tagModalOpen}
          position={tagModalPosition}
          onClose={() => setTagModalOpen(false)}
          cardId={cards[tagCardIndex]?.id}
          cardQuestionType="FIB"
          onTagAdded={handleTagAdded}
          onTagUpdated={handleTagUpdated}
          onTagDeleted={handleTagDeleted}
          onShowSnackbar={showSnackbar}
        />
      </div>

      <Modal open={infoModalOpen} onClose={() => setInfoModalOpen(false)}>
        <Box
          sx={{
            p: 0,
            bgcolor: "background.paper",
            mx: "auto",
            mt: "3%",
            mb: "3%",
            width: "90%",
            maxWidth: 800,
            maxHeight: "90vh",
            borderRadius: "16px",
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            "&::-webkit-scrollbar": {
              width: "10px",
              height: "10px",
            },
            "&::-webkit-scrollbar-track": {
              background: theme.palette.mode === 'dark' ? '#2d3748' : '#f1f5f9',
              borderRadius: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: theme.palette.mode === 'dark' ? '#475569' : '#94a3b8',
              borderRadius: "6px",
              border: `2px solid ${theme.palette.mode === 'dark' ? '#2d3748' : '#f1f5f9'}`,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: theme.palette.mode === 'dark' ? '#64748b' : '#64748b',
            },
            // Custom scrollbar for Firefox
            scrollbarWidth: "thin",
            scrollbarColor: theme.palette.mode === 'dark' ? '#475569 #2d3748' : '#94a3b8 #f1f5f9',
          }}

        >
          {/* Header */}
          <Box
            sx={{
              p: 3.5,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: "700", mb: 0.5 }}>
                User Guide
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Fill in the Blanks Questions
              </Typography>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ p: 4, overflowY: "auto", flex: 1 }}>
            {/* Introduction */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                  }}
                >
                  <i className="bi bi-info-circle" style={{ color: "white", fontSize: "20px" }}></i>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: "600", color: theme.palette.primary.main }}>
                  Introduction
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7, pl: 7 }}>
                Welcome to the Fill in the Blanks Questions page! This tool allows you to create, edit, delete, and organize fill-in-the-blank questions for quizzes. You can add questions, answers, explanations, and tags to categorize your content.
              </Typography>
            </Box>

            {/* Creating a Question */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                  }}
                >
                  <i className="bi bi-plus-circle" style={{ color: "white", fontSize: "20px" }}></i>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: "600", color: theme.palette.primary.main }}>
                  Creating a New Question
                </Typography>
              </Box>
              <Box sx={{ pl: 7, display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  1. Enter your question in the rich text editor. Use <strong>underscores (_)</strong> to mark blanks (e.g., "The capital of France is _.").
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  2. Then place the cursor at the end and Press <strong>Enter</strong> to submit the question and add options for each blank.
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  3. For each blank, add options, mark the correct answer, and provide explanations if needed.
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  4. Click <strong>Add Question</strong> to save.
                </Typography>
              </Box>
            </Box>

            {/* Managing Questions */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                  }}
                >
                  <i className="bi bi-gear" style={{ color: "white", fontSize: "20px" }}></i>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: "600", color: theme.palette.primary.main }}>
                  Managing Questions
                </Typography>
              </Box>
              <Box sx={{ pl: 7, display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  - Click the <strong>expand icon (<i className="bi bi-arrows-angle-expand" style={{ color: "#667eea", fontSize: "16px" }}></i>)</strong> to view the answer and explanation.
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  - Click the <strong>edit icon (<i className="bi bi-pencil-square" style={{ color: "#10b981", fontSize: "16px" }}></i>)</strong> to update a question.
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  - Click the <strong>delete icon (<i className="bi bi-trash3" style={{ color: "#ef4444", fontSize: "16px" }}></i>)</strong> to remove a question.
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  - Click the <strong>tag icon (<i className="bi bi-tags" style={{ color: "#8b5cf6", fontSize: "16px" }}></i>)</strong> to add or manage tags.
                </Typography>
              </Box>
            </Box>

            {/* Rich Text Editor */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                  }}
                >
                  <i className="bi bi-pencil-square" style={{ color: "white", fontSize: "20px" }}></i>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: "600", color: theme.palette.primary.main }}>
                  Using the Rich Text Editor
                </Typography>
              </Box>
              <Box sx={{ pl: 7, display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  The rich text editor allows you to format your questions, answers, and explanations with a variety of tools. Here's how to use them:
                </Typography>

                {/* Basic Formatting */}
                <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.text.primary, mt: 1 }}>
                  Basic Formatting:
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                    • <strong>Bold:</strong> Select text and click <strong>B</strong> or press <strong>Ctrl+B</strong>.
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                    • <em>Italic:</em> Select text and click <em>I</em> or press <strong>Ctrl+I</strong>.
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                    • <u>Underline:</u> Select text and click <u>U</u> or press <strong>Ctrl+U</strong>.
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                    • <s>Strikethrough:</s> Select text and click <s>S</s>.
                  </Typography>
                </Box>

                {/* Lists */}
                <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.text.primary, mt: 1 }}>
                  Lists:
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                    • <strong>Bullet List:</strong> Click the bullet list icon (<i className="bi bi-list-ul" style={{ fontSize: "14px" }}></i>).
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                    • <strong>Numbered List:</strong> Click the numbered list icon (<i className="bi bi-list-ol" style={{ fontSize: "14px" }}></i>).
                  </Typography>
                </Box>

                {/* Subscript and Superscript */}
                <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.text.primary, mt: 1 }}>
                  Subscript and Superscript:
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                    • <strong>Subscript:</strong> Select text and click <strong>X<sub>2</sub></strong> or use the shortcut <strong>Ctrl+,</strong>.
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                    • <strong>Superscript:</strong> Select text and click <strong>X<sup>2</sup></strong> or use the shortcut <strong>Ctrl+.</strong>.
                  </Typography>
                </Box>

                {/* LaTeX for Equations */}
                <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.text.primary, mt: 1 }}>
                  LaTeX for Equations:
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                    • <strong>Inline LaTeX:</strong> Wrap your equation in <code>$...$</code>. Example: <code>$E = mc^2$</code> renders as {` `}
                    <span dangerouslySetInnerHTML={{ __html: katex.renderToString("E = mc^2", { displayMode: false }) }}></span>.
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                    • <strong>Block LaTeX:</strong> Wrap your equation in <code>$$...$$</code>. Example: {` `}
                    <code>
                      $$
                      \int_a^b f(x) \,dx
                      $$
                    </code> renders as {` `}
                    <div dangerouslySetInnerHTML={{ __html: katex.renderToString("\\int_a^b f(x) \\,dx", { displayMode: true }) }}></div>.
                  </Typography>
                </Box>

                {/* Headers */}
                <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.text.primary, mt: 1 }}>
                  Headers:
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                    • Select text and choose a header size from the dropdown (e.g., <strong>H1, H2</strong>).
                  </Typography>
                </Box>

                {/* Indentation */}
                <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.text.primary, mt: 1 }}>
                  Indentation:
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                    • <strong>Increase Indent:</strong> Click the <strong>Increase Indent</strong> icon (<i className="bi bi-text-indent-right" style={{ fontSize: "14px" }}></i>).
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                    • <strong>Decrease Indent:</strong> Click the <strong>Decrease Indent</strong> icon (<i className="bi bi-text-indent-left" style={{ fontSize: "14px" }}></i>).
                  </Typography>
                </Box>

                {/* Text Alignment */}
                <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.text.primary, mt: 1 }}>
                  Text Alignment:
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                    • Align text to the <strong>left</strong>, <strong>center</strong>, or <strong>right</strong> using the alignment icons.
                  </Typography>
                </Box>

                {/* Code Blocks */}
                <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.text.primary, mt: 1 }}>
                  Code Blocks:
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                    • Click the <strong>Code Block</strong> icon (<i className="bi bi-code" style={{ fontSize: "14px" }}></i>) to insert a code block.
                  </Typography>
                </Box>

                {/* Colors and Background */}
                <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.text.primary, mt: 1 }}>
                  Colors and Background:
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                    • Change text color or background color using the color picker icons.
                  </Typography>
                </Box>

                {/* Links and Images */}
                {/* <Typography variant="subtitle2" sx={{ fontWeight: "600", color: "#2d3748", mt: 1 }}>
      Links and Images:
    </Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
      <Typography variant="body2" sx={{ color: "#718096", lineHeight: 1.7 }}>
        • <strong>Insert Link:</strong> Select text and click the link icon (<i className="bi bi-link" style={{ fontSize: "14px" }}></i>).
      </Typography>
      <Typography variant="body2" sx={{ color: "#718096", lineHeight: 1.7 }}>
        • <strong>Insert Image:</strong> Click the image icon (<i className="bi bi-image" style={{ fontSize: "14px" }}></i>).
      </Typography>
    </Box> */}

                {/* Clean Formatting */}
                {/* <Typography variant="subtitle2" sx={{ fontWeight: "600", color: "#2d3748", mt: 1 }}>
      Clean Formatting:
    </Typography>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 2 }}>
      <Typography variant="body2" sx={{ color: "#718096", lineHeight: 1.7 }}>
        • Click the <strong>Clean Formatting</strong> icon (<i className="bi bi-eraser" style={{ fontSize: "14px" }}></i>) to remove all formatting.
      </Typography>
    </Box> */}
              </Box>
            </Box>

            {/* Tips */}
            <Box
              sx={{
                p: 3,
                borderRadius: "12px",
                bgcolor: theme.palette.background.default,
                border: "1px solid #e2e8f0",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: "600", color: theme.palette.primary.main, mb: 2 }}>
                💡 Quick Tips
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  • Use underscores (_) to mark blanks in your question.
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  • Add explanations to help users understand the answer.
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  • Use tags to categorize questions (e.g., "Math", "Physics").
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              p: 3,
              bgcolor: theme.palette.background.paper,
              borderTop: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="contained"
              onClick={() => setInfoModalOpen(false)}
              sx={{
                minWidth: 120,
                borderRadius: "10px",
                padding: "10px 28px",
                fontWeight: "600",
                textTransform: "none",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                  boxShadow: "0 6px 16px rgba(102, 126, 234, 0.4)",
                },
              }}
            >
              Got it
            </Button>
          </Box>
        </Box>
      </Modal>

    </div>
  );
}

export default FillInTheBlanks;
